import { Loader2 } from 'lucide-react';
import { ModalOverlay } from '../ModalOverlay';
import { isPaperThemeActive } from '@/hooks/useThemeFlag';
import { cn } from '@/lib/utils';

interface BillingPlan {
  id: string;
  name: string;
  price: number;
}

interface CancelConfirmModalProps {
  currentPeriodEnd: number | null;
  cancelLoading: boolean;
  onCancel: () => void;
  onKeep: () => void;
}

const paperButtonBase =
  'flex-1 h-9 rounded-full border border-solid text-[12px] font-medium tracking-[0.02em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const legacyButtonBase =
  'flex-1 h-9 rounded-lg border text-[13px] transition-colors disabled:opacity-50';

const paperSecondaryButton =
  'border-[var(--hair)] text-[var(--fg)] hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)] hover:border-[var(--periwinkle)]';

const paperPrimaryButton =
  'border-[var(--fg)] bg-[var(--fg)] text-[var(--fg-inverse)] hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]';

const legacySecondaryButton =
  'border-white/[0.08] text-zinc-400 hover:bg-white/[0.03] hover:text-zinc-300';

const legacyPrimaryButton =
  'border-white/[0.08] text-zinc-300 hover:bg-white/[0.03]';

export const CancelConfirmModal = (props: CancelConfirmModalProps) => {
  const paper = isPaperThemeActive();
  const titleClass = paper
    ? 'text-[16px] font-medium text-[var(--fg)]'
    : 'text-[16px] font-medium text-zinc-100';
  const bodyClass = paper
    ? 'mt-2 text-[13px] leading-relaxed text-[var(--fg-3)]'
    : 'mt-2 text-[13px] text-zinc-400';
  const emphasisClass = paper ? 'font-medium text-[var(--fg)]' : 'font-medium text-zinc-200';

  return (
    <ModalOverlay className="z-[70]">
      <h2 className={titleClass}>Cancel subscription?</h2>
      <p className={bodyClass}>
        Your subscription will remain active until the end of the current billing period
        {props.currentPeriodEnd ? (
          <>
            , ending on{' '}
            <span className={emphasisClass}>
              {new Date(props.currentPeriodEnd * 1000).toLocaleDateString()}
            </span>
          </>
        ) : null}
        . After that, you'll be moved to the Free plan.
      </p>
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={props.onKeep}
          className={cn(
            paper ? paperButtonBase : legacyButtonBase,
            paper ? paperSecondaryButton : legacySecondaryButton,
          )}
        >
          Keep plan
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          disabled={props.cancelLoading}
          className={cn(
            paper ? paperButtonBase : legacyButtonBase,
            paper ? paperPrimaryButton : legacyPrimaryButton,
          )}
        >
          {props.cancelLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Cancel'}
        </button>
      </div>
    </ModalOverlay>
  );
};

interface PlanChangeConfirmModalProps {
  plan: BillingPlan;
  isPendingDowngrade: boolean;
  currentPeriodEnd: number | null;
  isDowngrading: boolean;
  isCanceling: boolean;
  prorationLoading: boolean;
  prorationPreview: { immediateChargeUsd: number; currency: string } | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const PlanChangeConfirmModal = (props: PlanChangeConfirmModalProps) => {
  const { plan, isPendingDowngrade } = props;
  const paper = isPaperThemeActive();
  const titleClass = paper
    ? 'text-[16px] font-medium text-[var(--fg)]'
    : 'text-[16px] font-medium text-zinc-100';
  const bodyClass = paper
    ? 'mt-2 text-[13px] leading-relaxed text-[var(--fg-3)]'
    : 'mt-2 text-[13px] text-zinc-400';
  const emphasisClass = paper ? 'font-medium text-[var(--fg)]' : 'font-medium text-zinc-200';
  const warningClass = paper
    ? 'mt-2 text-[13px] text-[#F58C7A]'
    : 'mt-2 text-[13px] text-amber-400';

  return (
    <ModalOverlay className="z-[70]">
      <h2 className={titleClass}>
        {isPendingDowngrade ? 'Confirm downgrade' : 'Confirm payment'}
      </h2>
      <p className={bodyClass}>
        You're about to {isPendingDowngrade ? 'downgrade' : 'switch'} to the{' '}
        <span className={emphasisClass}>{plan.name}</span> plan
        {plan.price > 0 ? (
          <>
            {' '}
            (<span className={emphasisClass}>${plan.price}/month</span>)
          </>
        ) : null}
        .
      </p>
      {props.isDowngrading && (
        <p className={warningClass}>
          Your scheduled downgrade will be cancelled.
        </p>
      )}
      {!props.isDowngrading && props.isCanceling && (
        <p className={warningClass}>
          Your scheduled cancellation will be removed.
        </p>
      )}
      <p className={bodyClass}>
        {isPendingDowngrade ? (
          props.currentPeriodEnd ? (
            <>
              You'll keep your current plan until{' '}
              <span className={emphasisClass}>
                {new Date(props.currentPeriodEnd * 1000).toLocaleDateString()}
              </span>
              , then switch to {plan.name}
              {plan.price > 0 ? ` at $${plan.price}/month` : ''}.
            </>
          ) : (
            'Your current plan will remain active until the end of your billing period.'
          )
        ) : props.prorationLoading ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" />
            Calculating charge…
          </span>
        ) : props.prorationPreview ? (
          <>
            You'll be charged{' '}
            <span className={emphasisClass}>
              ${props.prorationPreview.immediateChargeUsd.toFixed(2)}
            </span>{' '}
            now for the remainder of your billing period, then ${plan.price}/month going forward.
          </>
        ) : (
          'Your charges will be prorated for the current billing period.'
        )}
      </p>
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={props.onCancel}
          className={cn(
            paper ? paperButtonBase : legacyButtonBase,
            paper ? paperSecondaryButton : legacySecondaryButton,
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={props.onConfirm}
          className={cn(
            paper ? paperButtonBase : legacyButtonBase,
            paper
              ? paperPrimaryButton
              : 'border-transparent bg-white text-zinc-900 font-medium hover:opacity-90',
          )}
        >
          Confirm
        </button>
      </div>
    </ModalOverlay>
  );
};
